/* eslint-disable import/no-duplicates */
import '@naturehouse/design-system/components/protons/icon/Icon';
import '@naturehouse/design-system/components/atoms/date-field/DateField';
import '@naturehouse/design-system/components/atoms/impact-house-tag/ImpactHouseTag';
import '@naturehouse/design-system/components/atoms/travel-company-input/TravelCompanyInput';
import '@naturehouse/design-system/components/molecules/calendar-week-days/CalendarWeekDays';
import type Curtain from '@naturehouse/design-system/components/molecules/curtain/Curtain';
import '@naturehouse/design-system/components/molecules/show-more/ShowMore';
import '@naturehouse/design-system/components/molecules/slides-carousel/SlidesCarousel';
import '@naturehouse/design-system/components/organisms/image-carousel/ImageCarousel';
import { ImageCarouselEvent } from '@naturehouse/design-system/components/organisms/image-carousel/ImageCarousel';
import { ImageViewerEvents } from '@naturehouse/design-system/components/organisms/image-viewer/ImageViewer';
import '@naturehouse/design-system/components/templates/image-carousel-viewer/ImageCarouselViewer';
import type ImageCarouselViewer from '@naturehouse/design-system/components/templates/image-carousel-viewer/ImageCarouselViewer';
import { ImageCarouselViewerEvent } from '@naturehouse/design-system/components/templates/image-carousel-viewer/ImageCarouselViewer';
import { handleScrollToTargetElements } from '@naturehouse/nh-essentials/lib/dom/handleScrollToTargetElements';
import { isSameDomain } from '@naturehouse/nh-essentials/lib/url/isSameDomain';
import GTM from '../../gtm/GTM';
import '../../ui/web-components/travelParty/TravelParty';
import RecentlyViewedHousesManager from '../app/RecentlyViewedHousesManager';
import '../calendar/webComponents/CalendarInput';
import '../calendar/webComponents/DatePickerCalendar';
import type DatePickerCalendar from '../calendar/webComponents/DatePickerCalendar';
import Module from '../module';
import ModalComponent from '../shared/components/ModalComponent';
import './components/DetailForm';
import OpenNewChat from './components/OpenNewChat';
import './components/Translations/TranslatableContent';
import TranslatableContentManager from './components/Translations/TranslatableContentManager';
import './components/Translations/TranslatableContentSwitch';
import './components/Translations/TranslationFetcher';
import ArrivalDatesToCalendarConnector from './components/calendar/ArrivalDatesToCalendarConnector';
import MapsComponent from './components/mapsComponent';
import ReviewListManager from './components/reviews/ReviewListManager';
import ReviewPaginationHandler from './components/reviews/ReviewPaginationHandler';
import DetailBackButtonManager from './navigateBackButton/DetailBackButtonManager';
import SustainabilityDialogManager from './sustainability/SustainabilityDialogManager';
import '../../../css/views/listing.pcss';
import DetailPageLoginObserver from './DetailPageLoginObserver';
import TrackListing from './TrackListing';
import ListingDialogHandler, { type DialogConfig } from './components/ListingDialogHandler';
import GTMDetailImageNavigationModel from '../../gtm/models/GTMDetailImageNavigationModel';
import GTMDetailOpenFacilitiesButtonClickModel from '../../gtm/models/GTMDetailOpenFacilitiesButtonClickModel';
import GTMDetailOpenGoodToKnowButtonClickModel from '../../gtm/models/GTMDetailOpenGoodToKnowButtonClickModel';
import GTMDetailOpenActivityLabelsInfoButtonClickModel from '../../gtm/models/GTMDetailOpenActivityLabelsInfoButtonClickModel';
import GTMDetailImageClickModel from '../../gtm/models/GTMDetailImageClickModel';
import GTMDetailThumbnailClickModel from '../../gtm/models/GTMDetailThumbnailClickModel';
import GTMDetailContactLandlordButtonClickModel from '../../gtm/models/GTMDetailContactLandlordButtonClickModel';
import GTMDetailShowLocationButtonClickModel from '../../gtm/models/GTMDetailShowLocationButtonClickModel';
import GTMDetailHouseDescriptionReadMoreClickModel from '../../gtm/models/GTMDetailHouseDescriptionReadMoreClickModel';
import GTMDetailNatureDescriptionReadMoreClickModel from '../../gtm/models/GTMDetailNatureDescriptionReadMoreClickModel';
import GTMDetailReviewButtonClickModel from '../../gtm/models/GTMDetailReviewButtonClickModel';

/**
 * @Deprecated
 * This file is deprecated and should no longer be extended.
 */
export default class ListingModule extends Module {
    readonly #imageCarouselViewer = document.querySelector<ImageCarouselViewer>(
        'nh-image-carousel-viewer'
    );

    constructor() {
        super();

        this.components.push(MapsComponent);
        this.components.push(OpenNewChat);
        this.components.push(ModalComponent);

        TranslatableContentManager.getInstance();
        RecentlyViewedHousesManager.getInstance();

        ReviewPaginationHandler.getInstance();
        ReviewListManager.getInstance();
        DetailPageLoginObserver.init();

        const dialogConfigs: DialogConfig[] = [
            {
                buttonSelector: '[data-info-id="fireworks-free-info-dialog"]',
                dialogSelector: '[data-role="fireworks-free-info-dialog"]'
            },
            {
                buttonSelector: '[data-info-id="contactless-stay-info-dialog"]',
                dialogSelector: '[data-role="contactless-stay-info-dialog"]'
            },
            {
                buttonSelector: '[data-info-id="activity-labels-info-dialog"]',
                dialogSelector: '[data-role="activity-labels-info-dialog"]',
                eventModel: GTMDetailOpenActivityLabelsInfoButtonClickModel
            },
            {
                buttonSelector: '[data-role="open-facilities-modal"]',
                dialogSelector: '#detail-facilities-dialog',
                eventModel: GTMDetailOpenFacilitiesButtonClickModel
            },
            {
                buttonSelector: '[data-role="open-good-to-know-dialog"]',
                dialogSelector: '[data-role="detail-good-to-know-dialog"]',
                eventModel: GTMDetailOpenGoodToKnowButtonClickModel
            }
        ];

        new ListingDialogHandler().initialize(dialogConfigs);

        handleScrollToTargetElements();

        this.#imageCarouselViewer = document.querySelector<ImageCarouselViewer>(
            'nh-image-carousel-viewer'
        );

        const houseId = document.getElementById('house-id')?.getAttribute('content');
        if (houseId) {
            const sustainabilityDialogManager = new SustainabilityDialogManager(houseId);
            sustainabilityDialogManager.initialize();
        }

        this.#addImageCarouselTracking();
        this.#addImageViewerTracking();
        this.#addJumpToReviewButtonTracking();
        this.#addDescriptionReadMoreButtonTracking();
        this.#addContactWithLandlordButtonTracking();
        this.#addShowLocationButtonTracking();
        this.#addBookingDatesUnavailableTracking();

        if (!('IntersectionObserver' in window)) {
            return;
        }

        if (isSameDomain(document.referrer)) {
            DetailBackButtonManager.getInstance();
        }
    }

    public async init(): Promise<void> {
        super.init();

        await this.#initializeArrivalDatesToCalendarConnector();
    }

    readonly #initializeArrivalDatesToCalendarConnector = async (): Promise<void> => {
        // @see DetailForm.ts:85, the datepicker-calendar element is taken from a template and later added to the modal
        // the custom-elements API resolves the component too late before this code is executed,
        // therefore we have to wait until the datepicker-calendar is defined
        await customElements.whenDefined('datepicker-calendar');

        const datePickerCalendar = document.querySelector(
            'datepicker-calendar'
        ) as DatePickerCalendar | null;

        if (!datePickerCalendar) {
            return;
        }

        const selectedDateRange = datePickerCalendar.selectedDateRange;

        const arrivalDatesToCalendarConnector = new ArrivalDatesToCalendarConnector({
            startDate: datePickerCalendar.firstArrivalDate,
            endDate: selectedDateRange.end ?? selectedDateRange.start
        });

        await arrivalDatesToCalendarConnector.initialize();
    };

    readonly #addImageCarouselTracking = (): void => {
        if (!this.#imageCarouselViewer || !this.#imageCarouselViewer.carousel) {
            return;
        }

        this.#imageCarouselViewer.carousel.addEventListener(
            ImageCarouselEvent.NEXT_BUTTON_CLICK,
            this.#trackDetailNextImageEvent
        );

        this.#imageCarouselViewer.carousel.addEventListener(
            ImageCarouselEvent.PREV_BUTTON_CLICK,
            this.#trackDetailPrevImageEvent
        );

        this.#imageCarouselViewer.carousel.addEventListener(
            ImageCarouselEvent.NEXT_SWIPE_ACTION,
            this.#trackDetailNextImageEvent
        );

        this.#imageCarouselViewer.carousel.addEventListener(
            ImageCarouselEvent.PREV_SWIPE_ACTION,
            this.#trackDetailPrevImageEvent
        );

        this.#imageCarouselViewer.carousel.addEventListener(
            ImageCarouselEvent.THUMBNAIL_CLICK,
            (event: Event): void => {
                if (!(event instanceof CustomEvent)) {
                    return;
                }

                GTM.track(GTMDetailThumbnailClickModel.parseToGTMData());
            }
        );
    };

    readonly #addImageViewerTracking = (): void => {
        if (!this.#imageCarouselViewer || !this.#imageCarouselViewer.viewer) {
            return;
        }

        this.#imageCarouselViewer?.addEventListener(ImageCarouselViewerEvent.IMAGE_CLICK, () =>
            GTM.track(GTMDetailImageClickModel.parseToGTMData())
        );

        this.#imageCarouselViewer.viewer.addEventListener(
            ImageViewerEvents.NEXT_BUTTON_CLICK,
            this.#trackFullscreenNextImageEvent
        );

        this.#imageCarouselViewer.viewer.addEventListener(
            ImageViewerEvents.PREV_BUTTON_CLICK,
            this.#trackFullscreenPrevImageEvent
        );

        this.#imageCarouselViewer.viewer.addEventListener(
            ImageViewerEvents.NEXT_SWIPE_ACTION,
            this.#trackFullscreenNextImageEvent
        );

        this.#imageCarouselViewer.viewer.addEventListener(
            ImageViewerEvents.PREV_SWIPE_ACTION,
            this.#trackFullscreenPrevImageEvent
        );
    };

    readonly #addJumpToReviewButtonTracking = (): void => {
        const button = document.querySelector('[data-role="jump-to-reviews"]');
        const reviewsCurtain = document.querySelector<Curtain>(
            '[data-role="review-section"] details'
        );

        if (button === null || reviewsCurtain === null) {
            return;
        }

        button.addEventListener('click', (): void => {
            if (reviewsCurtain.open === false) {
                reviewsCurtain.toggle();
            }

            GTM.track(GTMDetailReviewButtonClickModel.parseToGTMData());
        });
    };

    readonly #addDescriptionReadMoreButtonTracking = (): void => {
        const houseDescriptionShowMore = document.querySelector('#house_description_show_more');
        const houseDescriptionShowMoreButton = houseDescriptionShowMore?.querySelector(
            'button[data-role="toggle"]'
        ) as HTMLButtonElement | null;

        const natureDescriptionShowMore = document.querySelector('#nature_description_show_more');
        const natureDescriptionShowMoreButton = natureDescriptionShowMore?.querySelector(
            'button[data-role="toggle"]'
        ) as HTMLButtonElement | null;

        if (houseDescriptionShowMoreButton) {
            this.#handleReadMoreTrackingOnButtonClick(houseDescriptionShowMoreButton, (): void => {
                GTM.track(GTMDetailHouseDescriptionReadMoreClickModel.parseToGTMData());
            });
        }

        if (natureDescriptionShowMoreButton) {
            this.#handleReadMoreTrackingOnButtonClick(natureDescriptionShowMoreButton, (): void => {
                GTM.track(GTMDetailNatureDescriptionReadMoreClickModel.parseToGTMData());
            });
        }
    };

    readonly #handleReadMoreTrackingOnButtonClick = (
        button: HTMLButtonElement,
        callback: () => void
    ): void => {
        button.addEventListener('click', (): void => {
            if (button.getAttribute('data-tracked') === 'true') {
                button.removeAttribute('data-tracked');
                return;
            }

            button.setAttribute('data-tracked', 'true');
            callback();
        });
    };

    readonly #addContactWithLandlordButtonTracking = (): void => {
        const button = document.querySelector('[data-role="contact-with-landlord"]');

        button?.addEventListener('click', (): void => {
            GTM.track(GTMDetailContactLandlordButtonClickModel.parseToGTMData());
        });
    };

    readonly #addShowLocationButtonTracking = (): void => {
        const button = document.querySelector('[data-initialize-map]');

        button?.addEventListener('click', (): void => {
            GTM.track(GTMDetailShowLocationButtonClickModel.parseToGTMData());
        });
    };

    readonly #addBookingDatesUnavailableTracking = (): void => {
        document.addEventListener('DOMContentLoaded', (): void => {
            TrackListing.trackBookingDatesUnavailableEvent();
        });
    };

    readonly #trackDetailNextImageEvent = (): void => {
        GTM.track(
            GTMDetailImageNavigationModel.parseToGTMData({
                direction: 'next',
                category: 'detail'
            })
        );
    };

    readonly #trackDetailPrevImageEvent = (): void => {
        GTM.track(
            GTMDetailImageNavigationModel.parseToGTMData({
                direction: 'prev',
                category: 'detail'
            })
        );
    };

    readonly #trackFullscreenNextImageEvent = (): void => {
        GTM.track(
            GTMDetailImageNavigationModel.parseToGTMData({
                direction: 'next',
                category: 'fullscreen'
            })
        );
    };

    readonly #trackFullscreenPrevImageEvent = (): void => {
        GTM.track(
            GTMDetailImageNavigationModel.parseToGTMData({
                direction: 'prev',
                category: 'fullscreen'
            })
        );
    };
}

(async function listingModuleInit(): Promise<void> {
    const listingModule: ListingModule = new ListingModule();
    await listingModule.init();
})();
